import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Bullet } from './Bullet';
import { makeGlowSprite } from './energyGlow';
import { useStore } from '../../state/store';
import type { Building } from '../Scene';

/**
 * Clockwork Colossus — towering steampunk humanoid. Walks across the terrain crushing
 * villages underfoot. Brass gear joints (4) take damage and slow it when destroyed,
 * but the only kill point is the **glowing crystal heart** behind a sliding chest panel
 * that opens for ~2s every 8s. Arms house twin gatling guns that hose the player.
 */
const HEART_HP = 60;
const GEAR_HP = 12;
/** The vent-attack cycle (replaces the old metronome panel): the colossus periodically HALTS and
 *  erupts scalding steam all around itself — a damaging AoE you must pull away from — and its
 *  chest panel is open exactly while it vents. Dodge the blast, dive the window. */
const VENT_PERIOD = 9.0;        // seconds between vents (closed-chest walking time)
const VENT_DURATION = 3.0;      // how long it vents (= how long the heart is exposed)
const VENT_RADIUS = 85;         // world units — the scalding cloud around it
const SPEED = 6.5;
const GATLING_FIRE_INTERVAL = 0.18;
const GATLING_RANGE = 800;
const STEAMROLL_HALF = 22;          // matches the 2x footprint (group-local, unscaled units)
const COLOSSUS_SCALE = 2;
/** Mesh-local foot soles sit at y≈-10.7 (×scale); the bob adds onto this base lift. */
const COLOSSUS_FOOT_LIFT = 10.7 * COLOSSUS_SCALE;
const SCORE = 9000;

interface Gear {
  id: string;
  hp: number;
  destroyed: boolean;
  group: THREE.Group;
}

type Stage = 'walking' | 'detonating' | 'done';

export class ClockworkColossus extends Entity {
  stage: Stage = 'walking';
  forward = new THREE.Vector3(0, 0, -1);
  readonly speed = SPEED;
  private heartHp = Math.round(HEART_HP * bossHpScale());
  private heartHpMax = this.heartHp;
  private ventDmgTimer = 0;
  private heart: THREE.Mesh;
  private heartCore: THREE.Mesh;
  private chestPanel: THREE.Object3D;
  private panelTimer = 4.0; // time until next state change
  private panelOpen = false;
  private gears: Gear[] = [];
  private leftGatling: THREE.Object3D;
  private rightGatling: THREE.Object3D;
  private gatlingCooldown = 0.5;
  private gatlingPhase = 0;
  private currentTarget: { x: number; z: number } | null = null;
  private steamrollSet = new WeakSet<Building>();
  private bobTimer = 0;
  private dieTimer = 0;
  private smokeTimer = 0;
  private mesh: THREE.Group;
  private speedMod = 1.0; // reduced as gears get destroyed
  private hips: THREE.Group[] = [];
  private knees: THREE.Group[] = [];

  constructor(spawnPos: THREE.Vector3) {
    super('tank');
    this.isBoss = true;
    this.hp = 999;
    this.radius = 30;
    this.group.position.copy(spawnPos); // spawnPos.y is the terrain surface (set by Game)

    const built = makeColossusMesh();
    this.group.add(built.mesh);
    this.mesh = built.mesh;
    this.heart = built.heart;
    this.heartCore = built.heartCore;
    this.chestPanel = built.chestPanel;
    this.gears = built.gears;
    for (const p of this.gears) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts
    this.leftGatling = built.leftGatling;
    this.rightGatling = built.rightGatling;
    this.hips = built.hips;
    this.knees = built.knees;

    // Heart and gear sub-parts only — no body catch-all. Bullets that miss these slip past.
    this.hitParts = [
      { id: 'heart', box: new THREE.Box3(new THREE.Vector3(-3, -3, -3), new THREE.Vector3(3, 3, 3)), object: this.heart },
      ...this.gears.map((gr) => ({
        id: gr.id,
        box: new THREE.Box3(new THREE.Vector3(-2.2, -2.2, -1.2), new THREE.Vector3(2.2, 2.2, 1.2)),
        object: gr.group,
      })),
    ];
  }

  alive(): boolean { return this.stage === 'walking'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'walking') return;
    if (partId === 'heart') {
      // Only damaged when the chest panel is open — blue shield spark + the timing tell.
      if (!this.panelOpen) {
        const wp = new THREE.Vector3();
        this.heart.getWorldPosition(wp);
        ctx.game.shieldedFeedback(ctx, wp, 'ARMORED — HIT THE HEART WHEN THE CHEST OPENS');
        return;
      }
      this.heartHp -= amount;
      const wp = new THREE.Vector3();
      this.heart.getWorldPosition(wp);
      new Explosion(wp, 5, 0.25, 'fire').addTo(ctx);
      if (this.heartHp <= 0) this.beginDying(ctx);
      return;
    }
    // (No body part — misses pass through.)
    const gear = this.gears.find((gg) => gg.id === partId);
    if (gear && !gear.destroyed) {
      gear.hp -= amount;
      const wp = new THREE.Vector3();
      gear.group.getWorldPosition(wp);
      new Explosion(wp, 4, 0.2, 'fire').addTo(ctx);
      if (gear.hp <= 0) {
        gear.destroyed = true;
        new Explosion(wp, 18, 0.6, 'fire').addTo(ctx);
        new Explosion(wp, 14, 3, 'smoke').setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
        ctx.game.audio.playBoom(0.5);
        if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== gear.id);
        gear.group.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) {
            const mat = m.material as THREE.MeshStandardMaterial;
            mat.color?.setHex(0x1c1814);
          }
        });
        // Each destroyed gear slows the colossus by 25%.
        this.speedMod = Math.max(0.15, this.speedMod - 0.25);
      }
    }
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'detonating') { this.updateDying(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Pulsate heart core
    const core = this.heartCore.material as THREE.MeshBasicMaterial;
    const pulse = 0.7 + 0.3 * Math.sin(performance.now() * 0.005);
    core.color.setRGB(pulse, 0.4 * pulse, 1.0);
    this.heartCore.scale.setScalar(0.85 + 0.18 * Math.sin(performance.now() * 0.005));

    // VENT-ATTACK CYCLE — the heart is exposed exactly while the colossus stops and erupts
    // scalding steam around itself. Enrage at half heart HP: tighter cycle, shorter window.
    const enraged = this.heartHp <= this.heartHpMax / 2;
    const ventPeriod = enraged ? 6.5 : VENT_PERIOD;
    const ventDuration = enraged ? 2.2 : VENT_DURATION;
    this.panelTimer -= dt;
    if (this.panelTimer <= 0) {
      this.panelOpen = !this.panelOpen; // panelOpen === "venting" (heart vulnerability gate)
      this.panelTimer = this.panelOpen ? ventDuration : (ventPeriod - ventDuration);
      const targetY = this.panelOpen ? 12 : 0;
      this.chestPanel.position.y = (this.chestPanel.userData.baseY ?? 0) + targetY;
      if (this.panelOpen) {
        // Eruption: a ring of steam around the whole machine, plus the chest burst.
        const wp = new THREE.Vector3();
        this.chestPanel.getWorldPosition(wp);
        new Explosion(wp, 22, 1.2, 'smoke').setDrift(new THREE.Vector3(0, 14, 0)).addTo(ctx);
        ctx.game.audio.playBoom(0.5);
      }
    }
    if (this.panelOpen) {
      // Scalding cloud: continuous steam FX + periodic damage ticks to anything friendly caught
      // inside VENT_RADIUS — the dodge half of the dodge-then-dive rhythm.
      if (Math.random() < 0.5) {
        const a = Math.random() * Math.PI * 2, r = 18 + Math.random() * (VENT_RADIUS * 0.55);
        new Explosion(
          this.group.position.clone().add(new THREE.Vector3(Math.cos(a) * r, 6 + Math.random() * 30, Math.sin(a) * r)),
          16, 0.8, 'smoke',
        ).setDrift(new THREE.Vector3(0, 16, 0)).addTo(ctx);
      }
      this.ventDmgTimer += dt;
      if (this.ventDmgTimer >= 0.45) {
        this.ventDmgTimer = 0;
        for (const e of ctx.game.entities.slice()) {
          if (!e.friendly || e.dead || !e.alive()) continue;
          if (e.position.distanceTo(this.group.position) <= VENT_RADIUS) e.damage(2, ctx);
        }
      }
    }

    // WALK GAIT — articulated stride, not a pogo hop. Phase rate is tied to actual ground speed
    // so the feet don't moonwalk: one half-cycle covers one stride length.
    const SWING = 0.28;                                        // hip swing amplitude (rad)
    const legLen = 15 * COLOSSUS_SCALE;                        // hip→sole, world units
    const stride = 2 * legLen * Math.sin(SWING);               // ground covered per half-cycle
    this.bobTimer += dt * (SPEED * this.speedMod * Math.PI * (this.panelOpen ? 0 : 1) * (enraged ? 1.4 : 1)) / stride;
    const phase = this.bobTimer;
    for (let i = 0; i < this.hips.length; i++) {
      const side = i === 0 ? 1 : -1;                           // legs swing in opposition
      const swing = Math.sin(phase) * SWING * side;
      this.hips[i].rotation.x = swing;
      // Knee flexes as that leg swings through (its hip rotates forward), straight when planted.
      const through = Math.max(0, Math.sin(phase) * side);
      this.knees[i].rotation.x = -through * 0.4;
    }
    // Subtle body motion: a small dip at double stride frequency (feet never leave the ground)
    // and a weight-shift roll onto the planted leg.
    this.mesh.position.y = COLOSSUS_FOOT_LIFT + (1 - Math.cos(phase * 2)) * 0.25 * COLOSSUS_SCALE;
    this.mesh.rotation.z = Math.sin(phase) * 0.035;

    // Pick a target village
    if (!this.currentTarget) {
      const v = pickVillage(this.group.position, ctx);
      if (v) this.currentTarget = { x: v.position.x, z: v.position.z };
    }
    if (this.currentTarget) {
      const dx = this.currentTarget.x - this.group.position.x;
      const dz = this.currentTarget.z - this.group.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 1) {
        const fx = dx / dist, fz = dz / dist;
        this.forward.lerp(new THREE.Vector3(fx, 0, fz), 0.04).normalize();
        const yaw = Math.atan2(this.forward.x, this.forward.z);
        this.group.rotation.y = yaw;
        // Halts while venting (planted, erupting); strides faster when enraged.
        const moveMul = (this.panelOpen ? 0 : 1) * (enraged ? 1.4 : 1);
        this.group.position.x += this.forward.x * SPEED * this.speedMod * moveMul * dt;
        this.group.position.z += this.forward.z * SPEED * this.speedMod * moveMul * dt;
      } else {
        this.currentTarget = null;
      }
    }
    // Keep the colossus's feet on the rolling terrain as it walks.
    this.group.position.y = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);

    // Steamroll buildings under the colossus's footprint.
    this.steamrollBuildings(ctx);

    // Gatling fire from arms — at the nearest friendly, so an armor push draws the guns.
    this.gatlingCooldown -= dt;
    const gatTarget = ctx.game.nearestFriendly(this.group.position, GATLING_RANGE);
    if (gatTarget && this.gatlingCooldown <= 0) {
      this.gatlingCooldown = enraged ? GATLING_FIRE_INTERVAL * 0.65 : GATLING_FIRE_INTERVAL;
      this.gatlingPhase = 1 - this.gatlingPhase;
      const muzzle = new THREE.Vector3();
      const arm = this.gatlingPhase === 0 ? this.leftGatling : this.rightGatling;
      arm.getWorldPosition(muzzle);
      const dir = gatTarget.position.clone().sub(muzzle).normalize();
      dir.x += (Math.random() - 0.5) * 0.05;
      dir.y += (Math.random() - 0.5) * 0.05;
      dir.z += (Math.random() - 0.5) * 0.05;
      dir.normalize();
      ctx.game.add(new Bullet(muzzle, dir.multiplyScalar(280), this.id, 'enemy'));
      // Spin barrels
      arm.rotation.z += 1.5;
    }

    // Steam vent puffs from shoulders
    this.smokeTimer += dt;
    if (this.smokeTimer >= 0.6) {
      this.smokeTimer = 0;
      for (const sx of [-7, 7]) {
        const local = new THREE.Vector3(sx, 30, 0);
        local.applyQuaternion(this.group.quaternion);
        new Explosion(this.group.position.clone().add(local), 5, 1.6, 'smoke')
          .setDrift(new THREE.Vector3(0, 10, 0)).addTo(ctx);
      }
    }
  }

  private steamrollBuildings(ctx: EntityCtx) {
    this.group.updateWorldMatrix(true, false);
    const inv = new THREE.Matrix4().copy(this.group.matrixWorld).invert();
    for (const v of ctx.game.gameScene.villages) {
      if (v.destroyed) continue;
      const dx = v.position.x - this.group.position.x;
      const dz = v.position.z - this.group.position.z;
      if (Math.hypot(dx, dz) > 200) continue;
      for (const bld of v.buildings) {
        if (bld.destroyed || this.steamrollSet.has(bld)) continue;
        const local = bld.position.clone().applyMatrix4(inv);
        if (Math.abs(local.x) <= STEAMROLL_HALF && Math.abs(local.z) <= STEAMROLL_HALF) {
          this.steamrollSet.add(bld);
          new Explosion(bld.position.clone(), 7, 1.4, 'smoke').setDrift(new THREE.Vector3(0, 5, 0)).addTo(ctx);
          bld.destroy(ctx.game.gameScene.root);
        }
      }
    }
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'detonating';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    const heartWp = new THREE.Vector3();
    this.heart.getWorldPosition(heartWp);
    new Explosion(heartWp, 60, 1.0, 'fire').addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    const t = this.dieTimer;
    // Topple forward as it dies
    if (t < 1.5) {
      this.group.rotation.x = Math.min(Math.PI / 2, t * 1.0);
    }
    // Build to a critical detonation at t=1.5
    if (t >= 1.5 && t < 1.6) {
      const center = this.group.position.clone();
      center.y = -460;
      // Vertical light spike
      new Explosion(center, 80, 0.6, 'fire').addTo(ctx);
      new Explosion(center.clone().add(new THREE.Vector3(0, 60, 0)), 50, 1.0, 'fire').addTo(ctx);
      new Explosion(center.clone().add(new THREE.Vector3(0, 120, 0)), 70, 1.2, 'fire').addTo(ctx);
    }
    if (t >= 1.6 && t < 1.7) {
      const center = this.group.position.clone();
      center.y = -460;
      new Explosion(center, 220, 1.2, 'fire').addTo(ctx);
      new Explosion(center, 140, 0.8, 'fire').addTo(ctx);
      ctx.game.audio.playBoom(1.6);
      // Ground shockwave — many spawned ring explosions
      for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2;
        const r = 30 + Math.random() * 30;
        const off = new THREE.Vector3(Math.cos(a) * r, 4, Math.sin(a) * r);
        new Explosion(center.clone().add(off), 50, 0.7, 'fire').addTo(ctx);
      }
    }
    // Pillar of smoke during the rest of the sequence
    if (t > 1.7 && t < 5) {
      if (Math.random() < 0.5) {
        const center = this.group.position.clone();
        center.y = -460;
        const off = new THREE.Vector3((Math.random() - 0.5) * 30, Math.random() * 100, (Math.random() - 0.5) * 30);
        new Explosion(center.clone().add(off), 50, 6, 'smoke')
          .setDrift(new THREE.Vector3(0, 18, 0)).addTo(ctx);
      }
    }
    if (t > 5) this.stage = 'done';
  }
}

function pickVillage(from: THREE.Vector3, ctx: EntityCtx) {
  let best = null;
  let bestD = Infinity;
  for (const v of ctx.game.gameScene.villages) {
    if (v.destroyed) continue;
    const dx = v.position.x - from.x;
    const dz = v.position.z - from.z;
    const d = Math.hypot(dx, dz);
    if (d < bestD) { bestD = d; best = v; }
  }
  return best;
}

/* ---------------------- mesh ---------------------- */

interface CBuilt {
  mesh: THREE.Group;
  heart: THREE.Mesh;
  heartCore: THREE.Mesh;
  chestPanel: THREE.Object3D;
  gears: Gear[];
  leftGatling: THREE.Object3D;
  rightGatling: THREE.Object3D;
  /** Articulated leg joints, driven by the walk gait: [left, right]. */
  hips: THREE.Group[];
  knees: THREE.Group[];
}

function makeColossusMesh(): CBuilt {
  const g = new THREE.Group();
  const armor = new THREE.MeshStandardMaterial({ color: 0x4a4032, roughness: 0.85, flatShading: true });
  const armorDark = new THREE.MeshStandardMaterial({ color: 0x2a221a, roughness: 0.9, flatShading: true });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb88a30, roughness: 0.5, metalness: 0.8 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.45, metalness: 0.7 });
  const heartGlow = new THREE.MeshBasicMaterial({ color: 0xa050ff });
  const dim = new THREE.MeshStandardMaterial({ color: 0x100808, roughness: 1 });

  // Body torso — broad, slightly tapered
  const torso = new THREE.Mesh(new THREE.BoxGeometry(13, 18, 9, 6, 8, 4), armor);
  torso.position.set(0, 16, 0);
  g.add(torso);
  // Pelvis
  const pelvis = new THREE.Mesh(new THREE.BoxGeometry(12, 4, 8, 6, 2, 4), armorDark);
  pelvis.position.set(0, 7, 0);
  g.add(pelvis);
  // Head
  const head = new THREE.Mesh(new THREE.BoxGeometry(5, 4, 5, 4, 4, 4), armor);
  head.position.set(0, 28, 0);
  g.add(head);
  // Brass faceplate
  const face = new THREE.Mesh(new THREE.BoxGeometry(3.5, 2.5, 0.4, 4, 4, 1), brass);
  face.position.set(0, 28, -2.5);
  g.add(face);
  // Eye-glow
  const eye = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 0.6), new THREE.MeshBasicMaterial({ color: 0xff8a30 }));
  eye.position.set(0, 28.3, -2.7);
  g.add(eye);

  // Chest panel + heart cavity behind it
  const cavityDepth = 1.6;
  const cavity = new THREE.Mesh(new THREE.BoxGeometry(7, 8, cavityDepth, 4, 4, 2), dim);
  cavity.position.set(0, 17, -3);
  g.add(cavity);
  // Heart sphere (hit-target) sits inside cavity
  const heart = new THREE.Mesh(new THREE.SphereGeometry(2.2, 32, 24), dim);
  heart.position.set(0, 17, -3);
  g.add(heart);
  // Glowing core inside (basic material, ignores light)
  const heartCore = new THREE.Mesh(new THREE.SphereGeometry(1.4, 32, 24), heartGlow);
  heartCore.position.copy(heart.position);
  heartCore.add(makeGlowSprite(0xff6a3a, 8.0));
  g.add(heartCore);
  // Sliding chest panel — slides UP when open
  const chestPanel = new THREE.Mesh(new THREE.BoxGeometry(8, 8.5, 0.6, 4, 4, 1), armor);
  chestPanel.position.set(0, 17, -2.0);
  chestPanel.userData.baseY = 17;
  g.add(chestPanel);
  // Brass collar around the panel
  const collar = new THREE.Mesh(new THREE.BoxGeometry(8.6, 9.2, 0.3, 4, 4, 1), brass);
  collar.position.set(0, 17, -1.7);
  g.add(collar);

  // 4 brass gear joints — left/right shoulders + left/right hips. Each is a thin
  // cylinder with brass spokes that visually rotate.
  const gears: Gear[] = [];
  const gearSpec: { id: string; pos: [number, number, number] }[] = [
    { id: 'gearLS', pos: [-7, 24, 0] },
    { id: 'gearRS', pos: [7, 24, 0] },
    { id: 'gearLH', pos: [-5, 6, 0] },
    { id: 'gearRH', pos: [5, 6, 0] },
  ];
  for (const spec of gearSpec) {
    const gg = new THREE.Group();
    const gear = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, 1.1, 24), brass);
    gear.rotation.z = Math.PI / 2;
    gg.add(gear);
    // Spokes
    for (let i = 0; i < 6; i++) {
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.4, 1.3, 1, 1, 1), ironDark);
      spoke.rotation.x = (i / 6) * Math.PI * 2;
      spoke.position.set(0, Math.cos((i / 6) * Math.PI * 2) * 1.2, Math.sin((i / 6) * Math.PI * 2) * 1.2);
      gg.add(spoke);
    }
    gg.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
    g.add(gg);
    gears.push({ id: spec.id, hp: GEAR_HP, destroyed: false, group: gg });
  }

  // Arms — long with a triple-barreled gatling at the wrist
  const leftArm = new THREE.Group();
  const lUpper = new THREE.Mesh(new THREE.BoxGeometry(3, 12, 3, 3, 6, 3), armor);
  lUpper.position.set(0, -6, 0);
  leftArm.add(lUpper);
  const lLower = new THREE.Mesh(new THREE.BoxGeometry(2.5, 9, 2.5, 3, 5, 3), armorDark);
  lLower.position.set(0, -16, 0);
  leftArm.add(lLower);
  const lGatling = new THREE.Group();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 4, 12), ironDark);
    barrel.position.set(Math.cos(a) * 0.5, -2, Math.sin(a) * 0.5);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -2;
    lGatling.add(barrel);
  }
  lGatling.position.set(0, -22, -2);
  lGatling.name = 'gatling'; // name survives clone() — index-based lookup is brittle
  leftArm.add(lGatling);
  leftArm.position.set(-9, 24, 0);
  g.add(leftArm);

  const rightArm = leftArm.clone();
  rightArm.position.set(9, 24, 0);
  // Find the cloned gatling by name (clone copies `.name`, unlike object refs in userData).
  const rGatling = (rightArm.getObjectByName('gatling') as THREE.Group) ?? rightArm;
  g.add(rightArm);

  // Legs — ARTICULATED: hip pivot → thigh → knee pivot → shin → foot, so the walker strides
  // (legs swing in opposition, knees flex on the swing-through) instead of pogo-hopping.
  // Geometry matches the old static columns: hip at y=5, sole bottoming out at y≈-10.7.
  const hips: THREE.Group[] = [];
  const knees: THREE.Group[] = [];
  for (const sx of [-3, 3]) {
    const hip = new THREE.Group();
    hip.position.set(sx, 5, 0);
    g.add(hip);
    const thigh = new THREE.Mesh(new THREE.BoxGeometry(4, 7, 4, 3, 3, 3), armor);
    thigh.position.set(0, -3.5, 0);
    hip.add(thigh);
    const knee = new THREE.Group();
    knee.position.set(0, -7, 0);
    hip.add(knee);
    const shin = new THREE.Mesh(new THREE.BoxGeometry(3.6, 8, 3.6, 3, 3, 3), armor);
    shin.position.set(0, -4, 0);
    knee.add(shin);
    const foot = new THREE.Mesh(new THREE.BoxGeometry(5, 1.4, 6, 4, 1, 4), armorDark);
    foot.position.set(0, -8, 0.5);
    knee.add(foot);
    hips.push(hip);
    knees.push(knee);
  }

  // Steam vents on shoulders (decorative)
  for (const sx of [-7, 7]) {
    const vent = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.85, 2.5, 16, 2), ironDark);
    vent.position.set(sx, 30, 0);
    g.add(vent);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.3, 16), brass);
    cap.position.set(sx, 31, 0);
    g.add(cap);
  }

  // Loom like a wave-18 boss: double the whole frame (hit boxes/muzzles ride their parents'
  // transforms). The foot soles sit at local y≈-10.7, so the bob code lifts the mesh by
  // COLOSSUS_FOOT_LIFT to plant them ON the terrain — pre-scale they were buried shin-deep.
  g.scale.setScalar(COLOSSUS_SCALE);
  return { mesh: g, heart, heartCore, chestPanel, gears, leftGatling: lGatling, rightGatling: rGatling, hips, knees };
}
